import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as mockDataService from '../../services/mockData';

export interface Product {
  id: string;
  code: string;
  type: 'produto' | 'servico';
  name: string;
  description: string;
  price: number;
  imageUrl?: string;
  videoUrl?: string;
  category?: string;
}

interface ProductState {
  items: Product[];
  loading: boolean;
  error: string | null;
}

const initialState: ProductState = {
  items: [],
  loading: false,
  error: null,
};

// Thunk para carregar produtos
export const fetchProductsAsync = createAsyncThunk(
  'products/fetchProductsAsync',
  async (_, { rejectWithValue }) => {
    try {
      const products = await mockDataService.getProducts();
      return products;
    } catch (error) {
      return rejectWithValue('Falha ao carregar os produtos');
    }
  }
);

// Thunk para adicionar um produto
export const addProductAsync = createAsyncThunk(
  'products/addProductAsync',
  async (product: Product) => {
    try {
      const savedProduct = await mockDataService.addProduct(product);
      return savedProduct;
    } catch (error) {
      throw new Error('Falha ao adicionar o produto');
    }
  }
);

// Thunk para deletar um produto
export const deleteProductAsync = createAsyncThunk(
  'products/deleteProductAsync',
  async (productId: string, { dispatch }) => {
    try {
      const success = await mockDataService.deleteProduct(productId);
      if (success) {
        dispatch(deleteProduct(productId));
      }
      return success;
    } catch (error) {
      throw new Error('Falha ao deletar o produto');
    }
  }
);

// Thunk para atualizar um produto
export const updateProductAsync = createAsyncThunk(
  'products/updateProduct',
  async (product: Product) => {
    try {
      const response = await mockDataService.updateProduct(product);
      return response;
    } catch (error) {
      throw error;
    }
  }
);

const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    addProduct: (state, action: PayloadAction<Product>) => {
      state.items.push(action.payload);
    },
    setProducts: (state, action: PayloadAction<Product[]>) => {
      state.items = action.payload;
    },
    deleteProduct: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.id !== action.payload);
    },
    updateProduct: (state, action: PayloadAction<Product>) => {
      const index = state.items.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateProductAsync.fulfilled, (state, action) => {
        const index = state.items.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(deleteProductAsync.fulfilled, (state, action) => {
        state.items = state.items.filter(p => p.id !== action.payload);
      })
      .addCase(addProductAsync.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(fetchProductsAsync.fulfilled, (state, action) => {
        state.items = action.payload;
      })
      .addCase(fetchProductsAsync.rejected, (state, action) => {
        state.error = action.payload as string | null;
      });
  },
});

export const { addProduct, setProducts, deleteProduct, updateProduct, setLoading, setError } = productSlice.actions;
export default productSlice.reducer; 